import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Row, Col } from 'react-bootstrap';

const Dryers = ({ theme, onAddDryer, permission, equipments }) => {
  const [formData, setFormData] = useState({
    nome: '',
    localizacao: '',
    equipamentos: []
  });
  const [equipmentPositions, setEquipmentPositions] = useState(
    Array(4).fill(null).map(() => Array(5).fill(''))
  );
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showSuccess]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleEquipmentChange = (row, col, value) => {
    setEquipmentPositions(prev => {
      const newPositions = [...prev];
      newPositions[row][col] = value;
      return newPositions;
    });

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors.equipamentos) {
      setErrors(prev => ({
        ...prev,
        equipamentos: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.nome.trim()) {
      newErrors.nome = 'Nome é obrigatório';
    }

    if (!formData.localizacao.trim()) {
      newErrors.localizacao = 'Localização é obrigatória';
    }

    if (!equipmentPositions.some(row => row.some(cell => cell.trim()))) {
      newErrors.equipamentos = 'Pelo menos um equipamento deve ser preenchido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Extrair equipamentos únicos da matriz de posições
    const uniqueEquipments = [];
    const positionsData = [];

    equipmentPositions.forEach((row, rowIndex) => {
      row.forEach((cell, colIndex) => {
        if (cell.trim()) {
          // Calcular posição numérica (1-20)
          const positionNumber = (rowIndex * 5) + (colIndex + 1);

          // Adicionar ao array de equipamentos se ainda não existir
          if (!uniqueEquipments.includes(cell.trim())) {
            uniqueEquipments.push(cell.trim());
          }

          // Adicionar dados de posição
          positionsData.push({
            serialnumber: cell.trim(),
            position: positionNumber
          });
        }
      });
    });

    // Preparar dados para envio
    const dryerData = {
      ...formData,
      equipamentos: uniqueEquipments
    };

    // Chamar a função de callback com os dados do secador
    if (onAddDryer) {
      onAddDryer(dryerData, positionsData);
    }

    // Limpar o formulário e mostrar mensagem de sucesso
    setFormData({
      nome: '',
      localizacao: '',
      equipamentos: []
    });
    setEquipmentPositions(Array(4).fill(null).map(() => Array(5).fill('')));
    setShowSuccess(true);
  };

  const handleReset = () => {
    setFormData({
      nome: '',
      localizacao: '',
      equipamentos: []
    });
    setEquipmentPositions(Array(4).fill(null).map(() => Array(5).fill('')));
    setErrors({});
  };

  return (
    <div>
      <Card>
        <Card.Header>
          <h5 className={`mt-2 mb-2 ${theme === 'dark' ? 'text-light' : 'text-dark'}`}>Adicionar Secador</h5>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className='g-2'>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className={`${theme === 'dark' ? 'text-light' : 'text-dark'}`}>Nome *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.nome}
                    onChange={(e) => handleInputChange('nome', e.target.value)}
                    isInvalid={!!errors.nome}
                    placeholder="Digite o nome do secador"
                    disabled={!permission}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.nome}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className={`${theme === 'dark' ? 'text-light' : 'text-dark'}`}>Localização *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.localizacao}
                    onChange={(e) => handleInputChange('localizacao', e.target.value)}
                    isInvalid={!!errors.localizacao}
                    placeholder="Digite a localização do secador"
                    disabled={!permission}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.localizacao}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
            <Row className='g-2'>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className={`${theme === 'dark' ? 'text-light' : 'text-dark'}`}>Equipamentos</Form.Label>
                  <div className="equipment-matrix border rounded p-3" style={{ backgroundColor: theme === 'dark' ? '#2b2b2b' : '#f8f9fa' }}>
                    {equipmentPositions.map((row, rowIndex) => (
                      <div key={rowIndex} className="d-flex gap-2 mb-2">
                        <div className="d-flex align-items-center me-2" style={{ minWidth: '80px' }}>
                          <small className="text-muted">Linha {rowIndex + 1}:</small>
                        </div>
                        {row.map((cell, colIndex) => {
                          const positionNumber = (rowIndex * 5) + (colIndex + 1);

                          // Obter todos os equipamentos já selecionados, exceto o da posição atual
                          const selectedEquipments = [];
                          equipmentPositions.forEach((r, rIndex) => {
                            r.forEach((c, cIndex) => {
                              if (c.trim() && (rIndex !== rowIndex || cIndex !== colIndex)) {
                                selectedEquipments.push(c.trim());
                              }
                            });
                          });

                          return (
                            <div key={colIndex} className="flex-fill">
                              <Form.Select
                                size="sm"
                                value={cell}
                                onChange={(e) => handleEquipmentChange(rowIndex, colIndex, e.target.value)}
                                disabled={!permission}
                                style={{ fontSize: '0.85rem' }}
                              >
                                <option value="">Pos {positionNumber}</option>
                                {equipments && equipments
                                  .filter(equipment =>
                                    equipment.serialnumber &&
                                    equipment.serialnumber.startsWith('PRAR') &&
                                    !selectedEquipments.includes(equipment.serialnumber)
                                  )
                                  .map((equipment) => (
                                    <option
                                      key={equipment.serialnumber}
                                      value={equipment.serialnumber}
                                    >
                                      {equipment.serialnumber}
                                    </option>
                                  ))}
                              </Form.Select>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                  {errors.equipamentos && (
                    <div className="text-danger mt-1" style={{ fontSize: '0.875rem' }}>
                      {errors.equipamentos}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Selecione os equipamentos para cada posição na matriz 4x5 (posições de 1 a 20). Os equipamentos únicos serão extraídos automaticamente e suas posições numéricas serão registradas.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-2">
              <Button
                type="submit"
                variant="success"
                disabled={!permission}
              >
                Adicionar Secador
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                onClick={handleReset}
                disabled={!permission}
              >
                Limpar
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Dryers;
